import * as i0 from '@angular/core';
import { Injectable, input, signal, computed, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import * as i1$1 from '@angular/platform-browser';
import { throwError, BehaviorSubject, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/common/http';
import * as i3 from '@digipay/ng-lib-memory-cache';
function ApiImageComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "img", 1);
    i0.ɵɵlistener("load", function ApiImageComponent_Conditional_0_Template_img_load_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.handleImageLoad());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(ctx_r1.classes());
    i0.ɵɵproperty("src", ctx_r1.image, i0.ɵɵsanitizeUrl)("ngStyle", ctx_r1.computedStyles())("draggable", false);
  }
}
function ApiImageComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "img", 2);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(ctx_r1.classes());
    i0.ɵɵproperty("src", ctx_r1.fallbackImage(), i0.ɵɵsanitizeUrl)("ngStyle", ctx_r1.computedStyles())("draggable", false);
  }
}
var ImageType = /*#__PURE__*/function (ImageType) {
  ImageType[ImageType["PNG"] = 0] = "PNG";
  ImageType[ImageType["SVG"] = 1] = "SVG";
  return ImageType;
}(ImageType || {});
const ImageBlobType = {
  [ImageType.PNG]: 'application/octet-stream',
  [ImageType.SVG]: 'image/svg+xml'
};
let ApiImageService = /*#__PURE__*/(() => {
  class ApiImageService {
    constructor(location, http, cacheService) {
      this.location = location;
      this.http = http;
      this.cacheService = cacheService;
      this.prefixUrl = '';
    }
    getImage(imagePath) {
      if (imagePath[0] !== '/') {
        imagePath = '/' + imagePath;
      }
      imagePath = this.prefixUrl + imagePath;
      // const url = this.location.prepareExternalUrl(imagePath);
      return this.http.get(imagePath, {
        responseType: 'blob'
      }).pipe(catchError(e => {
        return throwError(e.error);
      }));
    }
    clearFromCache(fileId) {
      try {
        this.cacheService.remove(`image__${fileId}`);
      } catch (e) {
        // silence
      }
    }
    static {
      this.ɵfac = function ApiImageService_Factory(t) {
        return new (t || ApiImageService)(i0.ɵɵinject(i1.Location), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.MemoryCacheService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ApiImageService,
        factory: ApiImageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ApiImageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ApiImageEfficientRequestService = /*#__PURE__*/(() => {
  class ApiImageEfficientRequestService {
    constructor() {
      this.loading = new BehaviorSubject({});
      this.loaded = new Subject();
      this.error = new Subject();
      this.loaded.asObservable().subscribe(val => {
        this.setLoading(val.key, false);
      });
    }
    setLoading(key, val) {
      const l = this.loading.getValue();
      l[key] = val;
    }
    isLoading(key) {
      const loading = this.loading.getValue();
      return loading.hasOwnProperty(key) && loading[key];
    }
    afterLoad(key) {
      const l = this.loading.getValue();
      l[key] = false;
      return response => {
        this.loaded.next({
          key,
          data: response
        });
      };
    }
    errorLoad(key) {
      const l = this.loading.getValue();
      l[key] = false;
      this.error.next({
        key
      });
    }
    static {
      this.ɵfac = function ApiImageEfficientRequestService_Factory(t) {
        return new (t || ApiImageEfficientRequestService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ApiImageEfficientRequestService,
        factory: ApiImageEfficientRequestService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ApiImageEfficientRequestService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ApiImageComponent = /*#__PURE__*/(() => {
  class ApiImageComponent {
    constructor(sanitizer, imageApi, cache, loaderService, cdr) {
      this.sanitizer = sanitizer;
      this.imageApi = imageApi;
      this.cache = cache;
      this.loaderService = loaderService;
      this.cdr = cdr;
      this.imageId = input();
      this.skeletonHeight = input();
      this.borderRadius = input();
      this.classes = input('');
      this.width = input();
      this.height = input();
      this.centered = input();
      this.showLoading = input(true);
      this.showFallbackImage = input(true);
      this.fallbackImage = input();
      this.imageLoadError = signal(false);
      this.styles = input();
      this.computedStyles = computed(() => {
        let customStyles = {
          ...this.styles()
        };
        if (this.width()) {
          customStyles = {
            ...customStyles,
            width: this.width()
          };
        }
        if (this.height()) {
          customStyles = {
            ...customStyles,
            height: this.height()
          };
        }
        if (this.borderRadius()) {
          customStyles = {
            ...customStyles,
            borderRadius: this.borderRadius()
          };
        }
        if (this.centered()) {
          customStyles = {
            ...customStyles,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block'
          };
        }
        return customStyles;
      });
      this.imageType = ImageType.PNG;
      this.imageLoaded = signal(false);
      this.imageDownloaded = signal(false);
      this.loadError = new EventEmitter();
      this.fullLoaded = new EventEmitter();
      this.baseUrl = '/digipay/api/files/';
      this.subscriptions = [];
      toObservable(this.imageId).subscribe({
        next: () => {
          this.getImageData();
        }
      });
    }
    ngOnInit() {
      this.getImageData();
    }
    ngOnDestroy() {
      this.errorSubscription?.unsubscribe();
      this.subscriptions.forEach(s => s.unsubscribe());
    }
    getImageData() {
      if (!this.imageId()) {
        return;
      }
      const CACHE_KEY = 'image__' + this.imageId();
      if (!this.cache.has(CACHE_KEY)) {
        const isLoading = this.loaderService.isLoading(this.imageId() ?? '');
        const afterLoad = this.loaderService.afterLoad(this.imageId() ?? '');
        if (!isLoading) {
          this.loaderService.setLoading(this.imageId() ?? '', true);
          const path = this.baseUrl + this.imageId();
          this.imageApi.getImage(path).subscribe(data => {
            afterLoad(data);
            this.afterRequest(CACHE_KEY, data);
          }, e => {
            this.loaderService.errorLoad(this.imageId());
            this.handleErrorState(e);
          });
        } else {
          this.subscriptions.push(this.loaderService.loaded.asObservable().subscribe(val => {
            if (val.key === this.imageId()) {
              // only listen for this imageId
              this.afterRequest(CACHE_KEY, val.data);
            }
          }));
          this.subscriptions.push(this.loaderService.error.asObservable().subscribe(val => {
            if (val.key === this.imageId()) {
              this.handleErrorState('load error');
            }
          }));
        }
      } else {
        this.imageLoaded.set(true);
        this.image = this.cache.get(CACHE_KEY);
        this.cdr.detectChanges();
      }
    }
    afterRequest(cacheKey, data) {
      this.imageLoaded.set(true);
      const blob = new Blob([data], {
        type: ImageBlobType[this.imageType]
      });
      blob.text().then(res => {
        if (res.startsWith('<?xml') || res.startsWith('<svg')) {
          const svgBlob = new Blob([data], {
            type: 'image/svg+xml'
          });
          this.image = this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(svgBlob));
          this.cache.put(cacheKey, this.image);
          this.cdr.detectChanges();
        } else {
          this.image = this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blob));
          this.cache.put(cacheKey, this.image);
          this.cdr.detectChanges();
        }
      });
    }
    handleImageLoad() {
      this.imageLoadError.set(false);
      this.imageDownloaded.set(true);
      this.fullLoaded.emit();
    }
    handleErrorState(e) {
      this.imageLoadError.set(true);
      this.loadError.emit(e);
    }
    static {
      this.ɵfac = function ApiImageComponent_Factory(t) {
        return new (t || ApiImageComponent)(i0.ɵɵdirectiveInject(i1$1.DomSanitizer), i0.ɵɵdirectiveInject(ApiImageService), i0.ɵɵdirectiveInject(i3.MemoryCacheService), i0.ɵɵdirectiveInject(ApiImageEfficientRequestService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ApiImageComponent,
        selectors: [["ui-api-image"]],
        hostVars: 10,
        hostBindings: function ApiImageComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            let tmp_1_0;
            let tmp_3_0;
            i0.ɵɵclassMap(ctx.classes());
            i0.ɵɵstyleProp("width", (tmp_1_0 = ctx.computedStyles()["width"]) !== null && tmp_1_0 !== undefined ? tmp_1_0 : "auto")("height", ctx.computedStyles()["height"] ? ctx.computedStyles()["height"] : !ctx.imageLoaded() && ctx.skeletonHeight() ? ctx.skeletonHeight() : "auto")("border-radius", (tmp_3_0 = ctx.computedStyles()["borderRadius"]) !== null && tmp_3_0 !== undefined ? tmp_3_0 : "unset")("background-image", ctx.imageLoaded() && ctx.imageDownloaded() || !ctx.showLoading() || ctx.showFallbackImage() && ctx.fallbackImage() && ctx.imageLoadError() ? "none" : "linear-gradient(90deg, var(--shimmer-first-color), var(--shimmer-second-color), var(--shimmer-third-color))");
          }
        },
        inputs: {
          imageId: [i0.ɵɵInputFlags.SignalBased, "imageId"],
          skeletonHeight: [i0.ɵɵInputFlags.SignalBased, "skeletonHeight"],
          borderRadius: [i0.ɵɵInputFlags.SignalBased, "borderRadius"],
          classes: [i0.ɵɵInputFlags.SignalBased, "classes"],
          width: [i0.ɵɵInputFlags.SignalBased, "width"],
          height: [i0.ɵɵInputFlags.SignalBased, "height"],
          centered: [i0.ɵɵInputFlags.SignalBased, "centered"],
          showLoading: [i0.ɵɵInputFlags.SignalBased, "showLoading"],
          showFallbackImage: [i0.ɵɵInputFlags.SignalBased, "showFallbackImage"],
          fallbackImage: [i0.ɵɵInputFlags.SignalBased, "fallbackImage"],
          styles: [i0.ɵɵInputFlags.SignalBased, "styles"],
          imageType: "imageType",
          baseUrl: "baseUrl"
        },
        outputs: {
          loadError: "loadError",
          fullLoaded: "fullLoaded"
        },
        decls: 2,
        vars: 1,
        consts: [["alt", "image", 3, "class", "src", "ngStyle", "draggable"], ["alt", "image", 3, "load", "src", "ngStyle", "draggable"], ["alt", "fallback-image", 3, "src", "ngStyle", "draggable"]],
        template: function ApiImageComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, ApiImageComponent_Conditional_0_Template, 1, 6, "img", 0)(1, ApiImageComponent_Conditional_1_Template, 1, 6);
          }
          if (rf & 2) {
            i0.ɵɵconditional(0, ctx.imageId() && ctx.imageLoaded() && !ctx.imageLoadError() ? 0 : ctx.showFallbackImage() && ctx.fallbackImage() && ctx.imageLoadError() ? 1 : -1);
          }
        },
        dependencies: [i1.NgStyle],
        styles: ["[_ngcontent-%COMP%]:root{--shimmer-first-color: #CED2DA;--shimmer-second-color: #F1F3F7;--shimmer-third-color: #CED2DA}[_ngcontent-%COMP%]:root   .inverted[_ngcontent-%COMP%]{--shimmer-first-color: #191C23;--shimmer-second-color: #444851;--shimmer-third-color: #191C23}[_ngcontent-%COMP%]:root   .dark[_ngcontent-%COMP%]{--shimmer-first-color: #191C23;--shimmer-second-color: #444851;--shimmer-third-color: #191C23}[_ngcontent-%COMP%]:root   .dark[_ngcontent-%COMP%]   .inverted[_ngcontent-%COMP%]{--shimmer-first-color: #CED2DA;--shimmer-second-color: #F1F3F7;--shimmer-third-color: #CED2DA}[_ngcontent-%COMP%]:root{--base-font-feature-settings: \"ss01\" on, \"liga\" off, \"clig\" off}[_nghost-%COMP%]{display:flex;align-items:center;justify-content:center;background-size:200%;animation:_ngcontent-%COMP%_shimmer 1.5s infinite reverse}img[_ngcontent-%COMP%]{display:block}@keyframes _ngcontent-%COMP%_shimmer{0%{background-position:-100% 0}50%{background-position:0 0}to{background-position:100% 0}}"],
        changeDetection: 0
      });
    }
  }
  return ApiImageComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ApiImageModule = /*#__PURE__*/(() => {
  class ApiImageModule {
    static {
      this.ɵfac = function ApiImageModule_Factory(t) {
        return new (t || ApiImageModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ApiImageModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return ApiImageModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of api-image
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ApiImageComponent, ApiImageEfficientRequestService, ApiImageModule, ApiImageService, ImageBlobType, ImageType };
